import { State, Action, StateContext, Selector, Store } from '@ngxs/store';
import { StoryStateModel } from './story.state.model';
import {
  LoadStorySuccess,
  SynchronizeChapters,
  ResetStory,
  LoadStoryChunk,
  SetIncomingStory,
  LoadStory,
  BuildStory,
  SetStoryText,
  SetStory,
  SetStoryId,
  LoadStoryChunkSuccess,
  LoadStoryFail,
  ImportStory,
  ReorderLoadChunks,
  ImportStorySuccess,
  SetStoryTags,
  HumanReadySDK
} from './story.actions';
import { StoryService } from '../story.service';
import { SetChapters, SelectChapter, RegisterChapterThumbnails, SetChapterReadyState } from './chapters/story.chapters.actions';
import { StoryChaptersState } from './chapters/story.chapters.state';
import { SetLabels } from './labels/story.labels.actions';
import { ChapterModel } from '../models/chapter.model';
import { LabelModel } from '../models/labels/label.model';
import { StoryChapterThumbnail } from '../models/story-chapter-thumbnail.model';
import { StoryModel } from '../models/story.model';
import { mergeModules } from '../merge/merge-modules';
import { Thumbnail } from 'src/app/shared/thumbnail-utils';
import { environment } from 'src/environments/environment';
import { SerializeModuleService } from 'src/app/edit/serialize-module.service';
import { TrimConfiguration } from 'src/app/edit/models/trim-config.model';
import { partition, get } from 'lodash';
import { ObjectId, NodeState } from 'src/app/engine/state/nodes/engine.nodes.state.model';
import { NodeActions } from 'src/app/engine/state/nodes/engine.nodes.actions';
import { EngineService } from 'src/app/engine/engine.service';
import { buildNodeState } from 'src/app/engine/state/nodes/build-node-state';
import { ChapterUtils } from 'src/app/shared/chapter-utils';
import { ParticleActions } from 'src/app/engine/state/particles/particles.actions';
import { ParticleSystem } from 'src/app/engine/models/particles/particle-system-types';
import { ParticlesStateModel } from 'src/app/engine/state/particles/particles.state.model';
import { TweenParticleLookupModel } from '../models/tween-particle-lookup.model';
import { ContentType } from '../models/content-type.enum';
import { Injectable } from '@angular/core';
import { mockQuestions } from 'src/app/quiz/mock-data';
import { QuizQuestion } from 'src/app/quiz/models/quiz-question.model';
import { QuizActions } from 'src/app/quiz/state/quiz.actions';
import { from, of } from 'rxjs';
import { DatabaseStorageEnum, LocalStorageEnum, StorageService } from 'src/app/shared/storage.service';
import { delay } from 'rxjs/operators';
import { createQuestionsFromChapters } from 'src/app/quiz/config/quiz';

declare const Human;


const initialState: StoryStateModel = {
  loading: false,
  incoming: {
    id: undefined,
    type: undefined
  },
  storyId: undefined,
  storyType: undefined,
  loadingChunk: null,
  loadChunks: [],
  loadChunksRemaining: [],
  thumbnailLookup: []
};

@State<StoryStateModel>({
  name: 'story',
  defaults: initialState
})
@Injectable()
export class StoryState {

  /**
   * Selectors
   */
  @Selector()
  static getStory(state: StoryStateModel) {
    return state.story;
  }

  @Selector()
  static getStoryId(state: StoryStateModel) {
    return state.storyId;
  }

  @Selector()
  static getLoadingChunk(state: StoryStateModel) {
    return state.loadingChunk;
  }

  @Selector()
  static getLoadChunks(state: StoryStateModel) {
    return state.loadChunks;
  }

  @Selector()
  static getLoadChunksRemaining(state: StoryStateModel) {
    return state.loadChunksRemaining;
  }

  @Selector()
  static getStoryInfo(state: StoryStateModel) {
    return {
      id: state.storyId,
      type: state.storyType
    };
  }

  @Selector()
  static getIncomingStoryInfo(state: StoryStateModel) {
    return state.incoming;
  }

  @Selector()
  static getStoryTags(state: StoryStateModel) {
    return get(state, 'story.tags', []);
  }


  /**
   * 
   * Actions
   */
  @Action(SetStoryText)
  setStoryText(context: StateContext<StoryStateModel>, action: SetStoryText) {
    const story = context.getState().story;

    if (story) {
      context.patchState({
        story: {
          ...story,
          displayName: action.displayName
        }
      });
    }
  }

  @Action(SetStoryTags)
  setStoryTags(context: StateContext<StoryStateModel>, { tags }: SetStoryTags) {
    const story = context.getState().story;

    if (story) {
      context.patchState({
        story: {
          ...story,
          tags
        }
      });
    }
  }

  @Action(SetStory)
  setStory(context: StateContext<StoryStateModel>, action: SetStory) {
    context.patchState({
      story: action.story
    });
  }

  @Action(SetStoryId)
  SetStoryId(context: StateContext<StoryStateModel>, action: SetStoryId) {
    context.patchState({
      storyId: action.id
    });
  }

  @Action(SetIncomingStory)
  setIncomingStory(context: StateContext<StoryStateModel>, action: SetIncomingStory) {
    const state = context.getState();
    const { id, type, reset } = action;

    if (id !== state.storyId || reset) {
      context.patchState({
        incoming: { id: id, type: type }
      });
    } else if (reset) {
      context.dispatch(new BuildStory(action.id, state.story, action.type));
    }
  }

  // load the entire incoming story and its assets
  @Action(LoadStory)
  loadStory(context: StateContext<StoryStateModel>, action: LoadStory) {
    const getStory$ = action.preview ?
      from(this.storageService.getDatabaseItem(DatabaseStorageEnum.Preview)) :
      this.storyService.getStory(action.id, action.type, action.lang);

    getStory$
      .subscribe(
        // success
        (story) => {
          context.dispatch(
            new LoadStorySuccess(
              story,
              action.id,
              action.type
            )
          );
        },
        // error
        () => {
          context.dispatch(new LoadStoryFail(action.id, action.type));
        });
  }

  // Put story into engine and kickoff asset loading. 
  @Action(LoadStorySuccess)
  loadStorySuccess(context: StateContext<StoryStateModel>, action: LoadStorySuccess) {
    this.storyService
      .unloadStory(null)
      .pipe(delay(0))
      .subscribe(() => {
        context.dispatch(new BuildStory(action.id, action.story, action.type));        
      });
  }

  private buildStoryWorkflow(
    context: StateContext<StoryStateModel>,
    storyId: string,
    story: StoryModel,
    storyType: ContentType,
    targetChapterIdx: number = 0,
    thumbnails = new Map()
  ) {

    // TODO:
    // mock quiz data in order to enter quiz mode in the viewer
    

    this.storyService.buildStory(storyId, story, true).subscribe(built => {
      // parse chapters from Human Engine
      const chapters: ChapterModel[] = this.storyService.getStoryChapters(storyId, storyType);

      // parse labels
      let labels: LabelModel[] = [];
      chapters.forEach((chapter) => {
        labels = [...labels, ...chapter.labels];
      });

      const config = this.storyService.getLoadConfig(storyId, targetChapterIdx);
      const loadChunks = this.storyService.getStoryLoadChunks(storyId, config);


      // patch story state
      context.patchState({
        storyId,
        story,
        loadChunks,
        loadChunksRemaining: loadChunks,
        incoming: { id: undefined, type: undefined }
      });

      // build nodeState for each chapter.
      const graph = this.engineService.getSceneGraph(Human.scene.rootObjects);
      const nodeStates = chapters.reduce<Record<string, Record<ObjectId, NodeState>>>((acc, chapter) => {
        acc[chapter.id] = buildNodeState(
          graph,
          chapter.showObjects,
          chapter.pickThroughObjects,
          chapter.synchronization
        );
        return acc;
      }, {});



      context.dispatch(new NodeActions.InitNodeState(nodeStates));
      context.dispatch(new NodeActions.SetActiveChapter(chapters[targetChapterIdx].id));
      context.dispatch(new SetChapters(chapters));
      context.dispatch(new RegisterChapterThumbnails(thumbnails));
      context.dispatch(new SetLabels(labels));
      context.dispatch(new SelectChapter(chapters[targetChapterIdx]));


      // set initial particle system state.
      const tweenParticleLookup: TweenParticleLookupModel = get(story, 'tweenParticleLookup', {});
      const particleSystems: ParticleSystem[] = get(story, 'customData.particleSystems', []);
      const assignedParticles: ParticlesStateModel['assignedParticles'] = chapters.reduce((acc, chapter) => {
        acc[chapter.id] = get(chapter, 'customData.particleSystems', []);
        return acc;
      }, {});

      context.dispatch(new ParticleActions.SetParticleState(
        particleSystems,
        assignedParticles,
        tweenParticleLookup
      ));

      // reset Quiz State
      context.dispatch(new QuizActions.ResetQuizState());
      // set Quiz Settings
      if (story.quizSettings) {
        context.dispatch(new QuizActions.SetQuizSettings(story.quizSettings));
      }

      // set Quiz
      const quizQuestions = createQuestionsFromChapters(chapters);
      context.dispatch(new QuizActions.SetQuestions(quizQuestions));

      context.dispatch(new HumanReadySDK());
      
      // kick off loading
      context.dispatch(new LoadStoryChunk());
      
    });
  }

  @Action(ImportStorySuccess)
  importStorySuccess(context: StateContext<StoryStateModel>, action: ImportStorySuccess) {

    const state = context.getState();
    const storyType = state.storyType;
    const storyId = state.storyId;

    const namespaceMap = {};
    const trimConfig = new TrimConfiguration();
    const replaceObjectId = false;
    const story = this.store.selectSnapshot(StoryState.getStory);
    const chapters = this.store.selectSnapshot(StoryChaptersState.getStoryChapters);
    const chapterIdMap = ChapterUtils.makeChapterIdMap(chapters, false);

    const currentStory = this.serializeModuleService.serialize(
      namespaceMap,
      trimConfig,
      replaceObjectId,
      story,
      chapters,
      chapterIdMap
    ).story;


    const newStory = mergeModules(
      currentStory,
      storyId,
      action.story,
      action.id,
      environment.exportType
    );



    // when a new module is imported, its thumbnails reference the origin module and chapters
    // we need a look up table to know where to get thumbnails before we save the module to a bookmark.
    // here we build the lookup table, and read it in Thumbnail.generateThumbnailUrls
    const originalThumbnails = currentStory.chapters.map((chapter, i) => {
      const newChapterId = chapter.id;
      const originalChapterId = chapter.id;
      const originalStoryId = storyId;
      return { newChapterId, originalChapterId, originalStoryId };
    });

 
    const thumbnailLookup = (state.thumbnailLookup || [])
      .concat(action.story.chapters.map((chapter, i) => {
        const startIndex = currentStory.chapters.length;
        const newChapterId = newStory.chapters[startIndex + i].id;
        const originalChapterId = chapter.id;
        const originalStoryId = action.id;
        return { newChapterId, originalChapterId, originalStoryId };
      }));

    context.patchState({
      thumbnailLookup
    });

    const thumbnails: Map<string, StoryChapterThumbnail> = Thumbnail.generateThumbnailUrls(
      [...originalThumbnails, ...thumbnailLookup]
    );

    

    // merge any thumbnails that currently live in state that are a blob
    const prevThumbnails = this.store.selectSnapshot(StoryChaptersState.getRegisteredThumbnails);
    const blobThumbnails = new Map([...prevThumbnails].filter(entry => entry[1].snapshot));
    const newThumbnails = new Map([...thumbnails, ...blobThumbnails]);


    // a story must be unloaded before it is rebuilt
    // we need to flush the engine's definition of the scene graph, because we made a new one in combineModules
    const purgeAssets = true; // we need to purge assets until parsing animations has been decoupled from fetching from the server
    this.storyService.unloadStory(null, purgeAssets).subscribe(() => {
      this.buildStoryWorkflow(context, 
        storyId, 
        <unknown>newStory as StoryModel, 
        storyType, 
        newStory.chapters.length - 1, newThumbnails);
    });
  }



  // Build story from story data. 
  @Action(BuildStory)
  buildStory(context: StateContext<StoryStateModel>, action: BuildStory) {
    // build and activate story into Human Engine
    const thumbnailLookup = action.story.chapters.map((chapter, i) => {
      const newChapterId = chapter.id;
      const originalChapterId = chapter.id;
      const originalStoryId = action.id;
      return { newChapterId, originalChapterId, originalStoryId };
    });

    const thumbnails: Map<string, StoryChapterThumbnail> = Thumbnail.generateThumbnailUrls(
      thumbnailLookup
    );

    const targetChapterIdx = action.chapterIdx || 0;
    this.buildStoryWorkflow(context, 
      action.id, 
      action.story, 
      action.type, 
      targetChapterIdx, 
      thumbnails);
  }

  @Action(LoadStoryChunk)
  loadStoryChunk(context: StateContext<StoryStateModel>) {
  
    // assume the chunk to load is in the zeroth index of loadChunksRemaining
    const chunkToLoad = context.getState().loadChunksRemaining[0];
    
    if (!chunkToLoad) {
      return;
    }

    // update the queue of remaining chunks.
    context.patchState({
      loadingChunk: chunkToLoad
    });

    // load assets through story Service
    // then, dispatch success and continue loading chunks
    this.storyService.loadStoryChunk(chunkToLoad, () => {
      context.dispatch([
        new LoadStoryChunkSuccess(chunkToLoad),
        new LoadStoryChunk()
      ]);
    });
  }

  /**
   * Reorder load chunks, so that the currently selected chapter's chunks are next in the queue.
   * This does not interrupt any currently loading chunks.
   */

  @Action(ReorderLoadChunks)
  reorderLoadChunks(context: StateContext<StoryStateModel>, action: ReorderLoadChunks) {
    const { loadChunksRemaining } = context.getState();

    const [chunksForActiveChapter, otherChunks] = partition(
      loadChunksRemaining,
      chunk => chunk.chapterId === action.chapterId
    );
    
    context.patchState({
      loadChunksRemaining:  [...chunksForActiveChapter, ...otherChunks]
    });
  }

  @Action(LoadStoryChunkSuccess)
  loadStoryChunkSuccess(context: StateContext<StoryStateModel>, action: LoadStoryChunkSuccess) {
    const loadChunksRemaining = context
      .getState()
      .loadChunksRemaining.filter(
        chunk => chunk.chunkId !== action.chunk.chunkId
      );
    
    context.patchState({
      loadingChunk: null,
      loadChunksRemaining
    });
    
    context.dispatch(new SetChapterReadyState(action.chunk.chapterId, action.chunk.readyState));
  }


  @Action(SynchronizeChapters)
  synchronizeChapters(context: StateContext<StoryStateModel>) {
    const chapters = this.store.selectSnapshot(StoryChaptersState.getStoryChapters);
    const { story } = context.getState();

    context.patchState({
      story: {
        ...story,
        chapters: [...chapters]
      }
    });
  }

  @Action(ImportStory)
  importStory(context: StateContext<StoryStateModel>, action: ImportStory) {
    this.storyService.getStory(action.id, action.type, action.lang)
      .subscribe(
        // success
        (story) => {
          context.dispatch(new ImportStorySuccess(story, action.id, action.type));
        });
  }

  @Action(ResetStory)
  resetStory(context: StateContext<StoryStateModel>) {
    context.setState(initialState);
  }


  constructor(
    private store: Store,
    private storyService: StoryService,
    private engineService: EngineService,
    private serializeModuleService: SerializeModuleService,
    private storageService: StorageService
  ) { }
}
